import React from 'react'
import { useCallback, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { createPortal } from 'react-dom'
import moment from 'moment-timezone'
import styles from './style.module.scss'
import * as calendarService from '../../modules/CalendarService'
import * as dateUtils from '../../utils/DateUtils'
import {
  getTodoInstances,
  addTodoChangeListener,
  removeTodoChangeListener,
} from '../../modules/TodoUtils'

import DayHeader from '../DayHeader'
import DayAllday from '../DayAllday'
import PeriodView from '../PeriodView'
import TimeLineView from '../TimeLineView'
import TimeMark from '../TimeMark'
import DayEventList from '../DayEventList'
import TimezoneNameBlock from '../TimezoneNameBlock'

const UPDATE_INTERVAL = 25000 // 현재 시간 업데이트 간격 (25초)
const SLOT_HEIGHT = 34
const MAX_HEIGHT = SLOT_HEIGHT * 2 * 24

interface RootState {
  settings: any
  appsettings: any
}

interface DayBoxOwnProps {
  date: Date
  onEditEvent: (
    date: Date,
    instanceId?: string | null,
    callback?: (() => void) | null,
    targetDates?: any,
    weekly?: boolean,
    isAllday?: boolean,
    onCancel?: () => void,
  ) => void
  onEventClick: (event: any, cellElement: HTMLDivElement | null) => void
  onEventDoubleClick: (event: any, cellElement: HTMLDivElement | null) => void
}

export interface DayBoxRef {
  sendExternalClick: (event: any, tryCount?: number) => void
  getHeaderHeight: () => number
}

const mapStateToProps = (state: RootState) => ({
  settings: state.settings,
  appsettings: state.appsettings,
})

const connector = connect(mapStateToProps, null, null, { forwardRef: true })
type PropsFromRedux = ConnectedProps<typeof connector>
type DayBoxProps = PropsFromRedux & DayBoxOwnProps

const DayBox = forwardRef<DayBoxRef, DayBoxProps>(
  ({ date, onEditEvent, onEventClick, onEventDoubleClick, settings }, ref) => {
    const [timeline, setTimeline] = useState([]) // eventCells 대신 timeline 배열 사용
    const [allday, setAllday] = useState([])
    const [currentTime, setCurrentTime] = useState(new Date())
    const [isToday, setIsToday] = useState(false)
    const dayBoxRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const resizeObserverRef = useRef(null)
    const timerRef = useRef(null)
    const timeLineContainerRef = useRef(null)

    // DayEventList 관련 상태 추가
    const [showEventsList, setShowEventsList] = useState(false)
    const [eventsListPosition, setEventsListPosition] = useState({ top: 0, left: 0 })
    const [eventsListAnchor, setEventsListAnchor] = useState(null)

    // 드래그 관련 상태 추가
    const isDragging = useRef(false)
    const isStreching = useRef(false)
    const startPosition = useRef({ x: 0, y: 0 })
    const endPosition = useRef(null)
    const [newEventArea, setNewEventArea] = useState([])
    const headerBlockRef = useRef(null)
    const [headerNewEventArea, setHeaderNewEventArea] = useState(null)
    const draggingEventDate = useRef({ start: null, end: null, fromAllday: false })
    const resizeTimerRef = useRef(null)

    // 현재 시간 위치 계산
    const getCurrentTimePosition = useCallback(() => {
      const hours = currentTime.getHours()
      const minutes = currentTime.getMinutes()

      // 분 단위로 변환
      const totalMinutes = hours * 60 + minutes

      // 30분 슬롯 기준으로 위치 계산 (1슬롯 = 30px)
      return (totalMinutes / 30) * SLOT_HEIGHT
    }, [currentTime])

    // 현재 시간 업데이트 함수
    const updateCurrentTime = useCallback(() => {
      parseIsToday()
      const now = new Date()
      setCurrentTime(now)
    }, [date])

    const parseIsToday = () => {
      const today = new Date()
      const isCurrentDay =
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()

      setIsToday(isCurrentDay)
      return isCurrentDay
    }

    useEffect(() => {
      parseIsToday()

      addDraggingEvent()
      return () => {
        removeDraggingEvent()
      }
    }, [date])

    const sendExternalClick = (event, tryCount = 0) => {
      if (tryCount > 1) {
        return
      }

      let eventCell = document.getElementById(event.id)
      if (eventCell == null) {
        // 검색으로 찾은 이벤트의 경우 instanceId 가 달라서 찾을 수 없는 경우가 있음
        // serverId 와 startDate 로 찾음
        const instance = calendarService.getInstances().find((instance) => {
          if (
            instance.serverId === event.serverId &&
            instance.startDate.getTime() === event.startDate.getTime()
          ) {
            return true
          }
          return false
        })
        if (instance) {
          eventCell = document.getElementById(instance.id)
          event = instance
        }
      }

      if (eventCell) {
        eventCell.scrollIntoView({ behavior: 'instant', block: 'nearest' })
        setTimeout(() => {
          onEventClick({ instanceId: event.id, date: date }, eventCell as HTMLDivElement)
        }, 100)
      } else {
        const overCountId = 'over-count-' + (moment as any)(event.startDate).format('YYYY-MM-DD')
        const index = allday.findIndex((alldayEvent) => alldayEvent.id === event.id)
        if (index !== -1) {
          const eventCell = document.getElementById(overCountId)
          const rect = eventCell.getBoundingClientRect()
          if (rect) {
            setEventsListPosition({
              top: dayBoxRef.current.getBoundingClientRect().top + 20,
              left: rect.left + (rect.width - 200) / 2,
            })
          }
          setEventsListAnchor(eventCell)
          setShowEventsList(true)

          setTimeout(() => {
            sendExternalClick(event, tryCount + 1)
          }, 50)
        } else {
          console.log('eventCell not found')
        }
      }
    }

    // 초기 스크롤 위치 설정 함수 (별도로 분리)
    const setInitialScrollPosition = useCallback(() => {
      // 오늘 날짜인지 확인
      const isToday = parseIsToday()

      if (isToday && scrollContainerRef.current) {
        const position = getCurrentTimePosition() - 100 // 현재 시간 위치에서 약간 위로 스크롤
        if (position > 0) {
          scrollContainerRef.current.scrollTop = position
        }
      } else if (scrollContainerRef.current) {
        // 오늘이 아닌 경우 9시 위치로 스크롤 (9시간 * 60px = 540px)
        scrollContainerRef.current.scrollTop = 540
      }
    }, [getCurrentTimePosition])

    // 이벤트 계산 함수
    const calculateEvents = useCallback(async () => {
      // 언마운트되었거나 DOM에서 분리된(detached) 인스턴스는 계산/렌더를 건너뛴다.
      // 라우터 전환 등으로 남은 유령 인스턴스가 emit마다 setState를 호출해
      // 실제 보이는 인스턴스의 렌더를 방해하는 것을 방지한다.
      if (!dayBoxRef.current?.isConnected) {
        return
      }

      // 현재 날짜의 시작과 끝
      const dayStart = new Date(date)
      dayStart.setHours(0, 0, 0, 0)

      const dayEnd = new Date(date)
      dayEnd.setHours(23, 59, 59, 999)

      // 캘린더 서비스에서 이벤트 가져오기
      const instances = calendarService.getInstances()

      const alldayEvents = [] // 종일 이벤트 배열
      const timelineEvents = [] // 타임라인 이벤트 배열

      // 현재 날짜에 해당하는 이벤트만 필터링 // 종일 이벤트와 일반 이벤트 분리
      instances.forEach((instance) => {
        const startDate = instance.startDate
        const endTime =
          instance.endDate.valueOf() - (startDate.valueOf() === instance.endDate.valueOf() ? 0 : 1)
        if (startDate < dayEnd && endTime >= dayStart.valueOf()) {
          if (instance.allday) {
            alldayEvents.push(instance)
          } else {
            timelineEvents.push(instance)
          }
        }
      })

      const todoInstances = await getTodoInstances(dayStart, dayEnd)
      if (todoInstances.length > 0) {
        alldayEvents.push(...todoInstances)
      }

      const locale = settings.general?.locale
      alldayEvents.sort((a, b) => {
        if (a.todo && !b.todo) {
          return 1
        }
        if (b.todo && !a.todo) {
          return 1
        }
        if (a.todo?.status === 'COMPLETED') {
          return 1
        }
        if (b.todo?.status === 'COMPLETED') {
          return -1
        }
        const sOffset = a.startDate - b.startDate
        if (sOffset !== 0) {
          return sOffset
        }
        const eOffset = b.endDate - a.endDate
        if (eOffset !== 0) {
          return eOffset
        }
        const aSubject = a.subject || ''
        const bSubject = b.subject || ''
        return aSubject.localeCompare(bSubject, locale)
      })

      // await(getTodoInstances) 사이에 언마운트/detach된 경우, 죽은 인스턴스가
      // setState를 호출하지 않도록 한 번 더 확인한다.
      if (!dayBoxRef.current?.isConnected) {
        return
      }
      // allday 배열과 timeline 배열 설정
      setAllday(alldayEvents)
      setTimeline(timelineEvents)
    }, [date])

    // 컴포넌트 마운트/언마운트 및 날짜 변경 시 이벤트 계산
    useEffect(() => {
      calculateEvents()

      // 이벤트 리스너 등록
      calendarService.addInstanceListener(calculateEvents)
      addTodoChangeListener(calculateEvents)

      // 리사이즈 옵저버 설정
      if (dayBoxRef.current) {
        resizeObserverRef.current = new ResizeObserver(() => {
          if (resizeTimerRef.current != null) {
            cancelAnimationFrame(resizeTimerRef.current)
          }
          resizeTimerRef.current = requestAnimationFrame(() => {
            calculateEvents()
            resizeTimerRef.current = null
          })
        })

        resizeObserverRef.current.observe(dayBoxRef.current)
      }

      return () => {
        // 이벤트 리스너 제거
        calendarService.removeInstanceListener(calculateEvents)
        removeTodoChangeListener(calculateEvents)
        // 리사이즈 옵저버 제거
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect()
        }
        if (resizeTimerRef.current != null) {
          cancelAnimationFrame(resizeTimerRef.current)
        }
      }
    }, [calculateEvents])

    // 현재 시간 업데이트를 위한 별도의 useEffect
    useEffect(() => {
      // 초기 업데이트
      updateCurrentTime()

      // 타이머 설정
      timerRef.current = setInterval(updateCurrentTime, UPDATE_INTERVAL)

      return () => {
        // 타이머 제거
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }, [updateCurrentTime])

    // 드래그 관련 함수들 추가
    const calcPoint = (e) => {
      const bound = timeLineContainerRef.current.getBoundingClientRect()

      const parentX = bound.left
      const parentY = bound.top
      const x = e.clientX - parentX
      const y = e.clientY - parentY

      return { x: x, y: y }
    }

    const calcTime = (baseDate, { y }, forEnd = false) => {
      console.log('inPos y', y)

      const inDate = new Date(baseDate)
      inDate.setHours(0, 0, 0, 0)

      let minute = null
      if (forEnd) {
        minute = Math.ceil(y / SLOT_HEIGHT) * 30
      } else {
        minute = Math.floor(y / SLOT_HEIGHT) * 30
      }
      inDate.setMinutes(minute)

      return inDate
    }

    const onMouseDown = (e) => {
      if (e.buttons === 1) {
        isDragging.current = true
        startPosition.current = calcPoint(e)
      }
    }

    const onMouseMove = (e) => {
      if (e.buttons === 1 && isDragging.current) {
        document.body.style.userSelect = 'none'
        const pos = calcPoint(e)
        if (pos === null) {
          return
        }

        const startPos = startPosition.current
        if (endPosition.current === null) {
          const offset = 15
          if (Math.abs(pos.y - startPos.y) > offset) {
            endPosition.current = pos
          } else {
            return
          }
        } else {
          endPosition.current = pos
        }

        const eventArea = []
        const endPos = pos

        const bound = timeLineContainerRef.current.getBoundingClientRect()
        let startY = startPos.y
        let endY = endPos.y

        if (startY > endY) {
          const temp = startY
          startY = endY
          endY = temp
        }

        startY = Math.floor(startY / SLOT_HEIGHT) * SLOT_HEIGHT
        endY = Math.ceil(endY / SLOT_HEIGHT) * SLOT_HEIGHT

        eventArea.push({
          x: 0,
          y: startY,
          width: bound.width,
          height: endY - startY,
        })

        setNewEventArea(eventArea)
      }
    }

    const getDate = () => {
      return new Date(date)
    }

    const onMouseUp = (e) => {
      if (isDragging.current) {
        isDragging.current = false

        const pos = calcPoint(e)
        if (pos === null || endPosition.current === null) {
          setNewEventArea([])
          return
        }

        const date = getDate()
        const time1 = calcTime(date, startPosition.current)
        const time2 = calcTime(date, pos, true)
        let startDate = time1
        let endDate = time2
        if (time2 <= time1) {
          startDate = time2
          if (!(startDate.getHours() === 0 && startDate.getMinutes() === 0)) {
            startDate = new Date(startDate.getTime() - 1000 * 60 * 30)
          }
          endDate = time1
          if (!(endDate.getHours() === 0 && endDate.getMinutes() === 0)) {
            endDate = new Date(endDate.getTime() + 1000 * 60 * 30)
          }
        }

        onEditEvent(startDate, null, null, { start: startDate, end: endDate }, false, false, () => {
          endPosition.current = null
          setNewEventArea([])
        })
      }
      document.body.style.userSelect = 'auto'
      setNewEventArea([])
      setHeaderNewEventArea(null)
    }

    const addDraggingEvent = () => {
      window.addEventListener('mouseup', onMouseUp)
    }

    const removeDraggingEvent = () => {
      window.removeEventListener('mouseup', onMouseUp)
    }

    // 스크롤 위치 설정을 위한 별도의 useEffect 수정
    useEffect(() => {
      setInitialScrollPosition()
    }, [])

    // 이벤트 클릭 핸들러
    const handleEventClick = (event, cellElement) => {
      if (event.isOverCount) {
        // 더보기 클릭 시  표시
        const rect = cellElement.getBoundingClientRect()
        if (rect) {
          setEventsListPosition({
            top: dayBoxRef.current.getBoundingClientRect().top + 20,
            left: rect.left + (rect.width - 200) / 2,
          })
        }
        setEventsListAnchor(cellElement)
        setShowEventsList(true)
      } else {
        onEventClick(event, cellElement)
      }
    }

    // DayEventList 닫기 핸들러
    const handleCloseEventsList = () => {
      setShowEventsList(false)
    }

    // DayEventList 렌더링 완료 핸들러
    const handleEventsListDidRender = (element) => {
      if (element && eventsListAnchor) {
        const anchorRect = eventsListAnchor.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()

        // 화면 밖으로 나가지 않도록 위치 조정
        let top = dayBoxRef.current.getBoundingClientRect().top + 20
        let left = anchorRect.left + (anchorRect.width - elementRect.width) / 2

        if (left + elementRect.width > window.innerWidth) {
          left = window.innerWidth - elementRect.width - 10
        }

        setEventsListPosition({ top, left })
      }
    }

    // ref를 통해 외부에서 접근할 수 있는 함수들을 노출
    useImperativeHandle(ref, () => ({
      sendExternalClick,
      getHeaderHeight: () => {
        return headerBlockRef.current.getBoundingClientRect().height
      },
    }))

    // Portal을 사용하여 overlay와 이벤트 목록을 body에 직접 렌더링
    const renderPortal = () => {
      if (!showEventsList) return null

      return createPortal(
        <>
          <div className={styles.overlay} onClick={() => setShowEventsList(false)} />
          <div
            className={styles.eventsListContainer}
            style={{
              position: 'fixed',
              top: `${eventsListPosition.top}px`,
              left: `${eventsListPosition.left}px`,
            }}
          >
            <DayEventList
              date={date}
              instances={allday}
              onClose={handleCloseEventsList}
              onDidRender={handleEventsListDidRender}
              onCellClick={onEventClick}
              onCellDoubleClick={onEventDoubleClick}
            />
          </div>
        </>,
        document.body,
      )
    }

    const handleDrop = (dropEvent, toAllday) => {
      const e = dropEvent
      const instanceId = e.dataTransfer.getData('data')
      const pos = calcPoint(e)
      if (pos === null) {
        setNewEventArea([])
        setHeaderNewEventArea(null)
        return
      }

      const instance = calendarService.getInstance(instanceId)
      if (!instance) {
        console.log('instance not found', instanceId)
        setNewEventArea([])
        setHeaderNewEventArea(null)
        return
      }

      let newEndTime = null
      let toTime = null

      if (!toAllday) {
        toTime = calcTime(date, pos)

        let duration = instance.endDate.getTime() - instance.startDate.getTime()
        if (instance.allday) {
          duration = settings.calendar.defaultDuration * 60 * 1000
        }

        newEndTime = new Date(toTime.getTime() + duration)
      } else {
        toTime = new Date(date)
        toTime.setHours(0, 0, 0, 0)
        let duration = 1 * 24 * 60 * 60 * 1000
        const diff = dateUtils.betweenDay(instance.startDate, instance.endDate)
        if (diff !== 0) {
          duration = (diff + (instance.allday ? 0 : 1)) * 24 * 60 * 60 * 1000
        }

        newEndTime = new Date(toTime.getTime() + duration)
      }

      calendarService.updateEventChangeTime(instance, toTime, newEndTime, toAllday, (success) => {
        if (success) {
          console.log('update event success', instanceId)
        } else {
          console.log('update event failed', instanceId)
        }
      })
      setNewEventArea([])
      setHeaderNewEventArea(null)
    }

    const handleDragStart = (e, fromAllday = false) => {
      const instanceId = e.dataTransfer.getData('data')
      const instance = calendarService.getInstance(instanceId)
      if (!instance) {
        console.log('instance not found', instanceId)
        draggingEventDate.current = { start: null, end: null, fromAllday: false }
        return
      }
      draggingEventDate.current = {
        start: instance.startDate,
        end: instance.endDate,
        fromAllday: fromAllday,
      }
    }

    const handleDragMove = (e) => {
      let isAllday = false

      const bound = timeLineContainerRef.current.getBoundingClientRect()
      const pBound = timeLineContainerRef.current.parentElement.getBoundingClientRect()
      let visibleArea = {
        x: 0,
        y: pBound.top - bound.top,
        width: pBound.width,
        height: pBound.height,
      }

      const parentY = bound.top
      const y = e.clientY - parentY

      if (y < visibleArea.y) {
        // 커서가 화면을 벗어나지 않은지 확인.
        if (e.clientY !== 0 && e.screenY !== 0) {
          isAllday = true
        }
      }

      const pos = calcPoint(e)
      if (pos === null) {
        setNewEventArea([])
        setHeaderNewEventArea(null)
        return
      }

      if (isAllday) {
        const height = headerBlockRef.current.getBoundingClientRect().height

        setHeaderNewEventArea({
          x: 95,
          y: 0,
          width: bound.width,
          height: height,
        })
        setNewEventArea([])
      } else {
        const startY = Math.floor(pos.y / SLOT_HEIGHT) * SLOT_HEIGHT

        const draggingDate = draggingEventDate.current
        const duration = draggingDate.fromAllday
          ? settings.calendar.defaultDuration * 60 * 1000
          : draggingDate.end.getTime() - draggingDate.start.getTime()
        const slotCount = Math.ceil(duration / 1000 / 60 / 30)
        const containerHeight = MAX_HEIGHT
        let remainSlotHeight = SLOT_HEIGHT * slotCount

        const newEventAreas = []
        let posY = startY
        let slotHeight = remainSlotHeight

        if (slotHeight + startY > containerHeight) {
          slotHeight = containerHeight - startY
        }

        do {
          newEventAreas.push({
            x: 0,
            y: posY,
            width: bound.width,
            height: slotHeight,
          })
          posY = 0
          remainSlotHeight -= slotHeight
          slotHeight = remainSlotHeight > containerHeight ? containerHeight : remainSlotHeight
        } while (slotHeight > 0)

        setNewEventArea(newEventAreas)
        setHeaderNewEventArea(null)
      }
    }

    const calcPosFromDate = (aDate) => {
      const y = ((aDate.getHours() * 60 + aDate.getMinutes()) / 30) * SLOT_HEIGHT
      return { x: 0, y: y }
    }

    const onEventStrechStart = (event, isTop, isLeft, e) => {
      if (e.buttons === 1) {
        isStreching.current = true
        startPosition.current = calcPoint(e)
      }
    }

    const onEventStrechMove = (event, isTop, isLeft, e) => {
      if (e.buttons !== 1 || !isStreching.current) {
        return
      }

      const pos = calcPoint(e)
      if (pos === null) {
        return
      }
      console.log('pos', pos)

      const bound = timeLineContainerRef.current.getBoundingClientRect()
      if (pos.y <= 0) {
        pos.y = 1
      } else if (pos.y > MAX_HEIGHT) {
        pos.y = MAX_HEIGHT
      }

      let topTime = null
      let bottomTime = null
      let startY = null
      let endY = null

      if (isTop) {
        topTime = calcTime(date, pos)
        bottomTime = event.endDate
        if (!dateUtils.isSameDay(date, bottomTime)) {
          bottomTime = new Date(bottomTime)
          bottomTime.setHours(23, 59, 59, 999)
        }
        if (topTime >= bottomTime) {
          topTime = new Date(bottomTime.getTime() - 1000 * 60 * 30)
        }
        startY = calcPosFromDate(topTime).y
        endY = calcPosFromDate(bottomTime).y
      } else {
        topTime = event.startDate
        if (!dateUtils.isSameDay(date, topTime)) {
          topTime = dateUtils.getDateZero(date)
        }
        bottomTime = calcTime(date, pos, true)
        if (
          bottomTime.getFullYear() === date.getFullYear() &&
          bottomTime.getMonth() === date.getMonth() &&
          bottomTime.getDate() === date.getDate() + 1
        ) {
          bottomTime = new Date(date)
          bottomTime.setHours(23, 59, 59, 999)
        }
        if (topTime >= bottomTime) {
          bottomTime = new Date(topTime.getTime() + 1000 * 60 * 30)
        }
        startY = calcPosFromDate(topTime).y
        endY = calcPosFromDate(bottomTime).y
      }

      const eventArea = [
        {
          x: 0,
          y: startY,
          width: bound.width,
          height: endY - startY,
          strechInfo: { isTop },
        },
      ]

      setNewEventArea(eventArea)
    }

    const onEventStrechEnd = (event, isTop, isLeft, e, isCancel = false) => {
      if (isCancel) {
        _completeStrech()
        return
      }

      const pos = calcPoint(e)
      if (pos === null) {
        _completeStrech()
        return
      }

      const bound = timeLineContainerRef.current.getBoundingClientRect()
      const pBound = timeLineContainerRef.current.parentElement.getBoundingClientRect()
      let visibleArea = {
        x: 0,
        y: pBound.top - bound.top,
        width: pBound.width,
        height: pBound.height,
      }

      let y = pos.y
      if (y < visibleArea.y || y > visibleArea.y + visibleArea.height) {
        _completeStrech()
        return
      }
      if (y === 0) {
        y = 0
      } else if (y > MAX_HEIGHT) {
        y = MAX_HEIGHT
      }

      let topTime = null
      let bottomTime = null

      if (isTop) {
        topTime = calcTime(date, pos)
        bottomTime = event.endDate
        if (topTime >= bottomTime) {
          topTime = new Date(bottomTime.getTime() - 1000 * 60 * 30)
        }
      } else {
        topTime = event.startDate
        bottomTime = calcTime(date, pos, true)
        if (topTime >= bottomTime) {
          bottomTime = new Date(topTime.getTime() + 1000 * 60 * 30)
        }
      }

      const instanceId = event.instanceId
      const instance = calendarService.getInstance(instanceId)
      if (!instance) {
        console.log('instance not found', instanceId)
        _completeStrech()
        return
      }
      calendarService.updateEventChangeTime(instance, topTime, bottomTime, false, (success) => {
        if (success) {
          console.log('update event success', instanceId)
        } else {
          console.log('update event failed', instanceId)
        }
        _completeStrech()
      })
    }

    const _completeStrech = () => {
      isStreching.current = false
      startPosition.current = null
      endPosition.current = null
      setNewEventArea([])
    }

    const handleDoubleClick = (e, argDate, isAllday = false) => {
      console.log('handleDoubleClick', isAllday, date)

      let targetDates = null
      if (!isAllday) {
        const duration = settings.calendar.defaultDuration * 60 * 1000
        targetDates = {
          start: argDate,
          end: new Date(argDate.getTime() + duration),
        }
      }

      onEditEvent(date, null, null, targetDates, false, isAllday, () => {
        setNewEventArea([])
        setHeaderNewEventArea(null)
      })
    }

    const handleAlldayDoubleClick = (e) => {
      handleDoubleClick(e, null, true)
    }

    return (
      <div className={styles.dayBox} ref={dayBoxRef}>
        {/* 상단 블록 (Empty, Day Info, Allday Container) */}
        <div className={styles.headerBlock} ref={headerBlockRef}>
          {/* Timezone Name 셀 */}
          <TimezoneNameBlock targetDate={date} />

          {/* Day Info와 Allday Container */}
          <div
            className={styles.headerContent}
            onDrop={(e) => {
              handleDrop(e, true)
            }}
            onDragOver={(e) => {
              e.preventDefault()
            }}
            onDragStart={(e) => {
              handleDragStart(e, true)
            }}
            onDrag={(e) => {
              handleDragMove(e)
            }}
          >
            {/* 날짜 헤더 */}
            <DayHeader date={date} isToday={isToday} onDoubleClick={handleAlldayDoubleClick} />

            {/* 종일 이벤트 컨테이너 */}
            <DayAllday
              allday={allday}
              date={date}
              onEventClick={handleEventClick}
              onDoubleClick={handleAlldayDoubleClick}
              onEventDoubleClick={onEventDoubleClick}
            />
            {headerNewEventArea && (
              <div
                className={styles.newEvent}
                style={{
                  top: `${headerNewEventArea.y}px`,
                  left: `${headerNewEventArea.x}px`,
                  width: `${headerNewEventArea.width}px`,
                  height: `${headerNewEventArea.height}px`,
                }}
              ></div>
            )}
          </div>
        </div>

        {/* 하단 컨테이너 (Period, Timeline) */}
        <div className={styles.contentContainer}>
          {/* 스크롤 컨테이너 추가 */}
          <div className={styles.scrollContainer} ref={scrollContainerRef}>
            {/* 왼쪽 시간대 표시 영역 */}
            <PeriodView targetDate={date} />
            {/* 타임라인 뷰 */}
            <div
              className={styles.timelineContainer}
              draggable={false}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onDrop={(e) => {
                handleDrop(e, false)
              }}
              onDragOver={(e) => {
                e.preventDefault()
              }}
              onDragStart={(e) => {
                handleDragStart(e, false)
              }}
              onDrag={(e) => {
                handleDragMove(e)
              }}
              ref={timeLineContainerRef}
            >
              <TimeLineView
                timeline={timeline}
                date={date}
                onEventClick={onEventClick}
                onStrechStart={onEventStrechStart}
                onStrechMove={onEventStrechMove}
                onStrechEnd={onEventStrechEnd}
                onDoubleClick={handleDoubleClick}
                onEventDoubleClick={onEventDoubleClick}
              />
              {/* 현재 시간 표시 */}

              {newEventArea.map((area, index) => (
                <div
                  key={`newEvent-${index}`}
                  className={styles.newEvent}
                  style={{
                    position: 'absolute',
                    top: area.y + 'px',
                    left: area.x + 'px',
                    width: area.width + 'px',
                    height: area.height + 'px',
                  }}
                >
                  {area.strechInfo && (
                    <div
                      className={`${styles.strechHandle} ${
                        area.strechInfo.isTop ? styles.strechHandleTop : styles.strechHandleBottom
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            {isToday && (
              <TimeMark currentTime={currentTime} getCurrentTimePosition={getCurrentTimePosition} />
            )}
          </div>
        </div>

        {/* Portal을 사용하여 overlay와 이벤트 목록을 body에 직접 렌더링 */}
        {renderPortal()}
      </div>
    )
  },
)

export default connector(DayBox)
